import { Injectable } from '@angular/core';

import {
  SuburbAreasService,
  SuburbPageRef,
  SuburbRegion,
  SuburbSubRegion,
} from '../../../../services/suburb-areas.service';
import { MapPage, MapRegion, MapSubRegion } from '../map/suburb-map.engine';
import { colourSlot, cssToken, mapRegions, mapSubRegions, rgbCss } from './map-colours';
import { WorkspaceStateService } from './workspace-state.service';

export interface Option {
  label: string;
  value: string;
}

export interface OptionGroup {
  label: string;
  items: Option[];
}

interface Meta {
  regions: SuburbRegion[];
  pages: SuburbPageRef[];
  subRegions?: SuburbSubRegion[];
}

/**
 * The regions, sub-regions and suburb pages the workspace works with (#256, #260), as the server lists them
 * (GET /website/suburb-areas/meta), with their names, colours and the pickers' options. Reloaded after a region, page
 * or sub-region save, and handed to the map.
 */
@Injectable()
export class SuburbCatalogueService {
  regions: MapRegion[] = [];
  regionNames = new Map<string, string>();
  /** The List's region filter. */
  regionOptions: Array<{ label: string; value: string }> = [];
  pageRefs: SuburbPageRef[] = [];
  /** Sub-regions (#260): as the server lists them (records), and as the map draws them. */
  subRegionRefs: SuburbSubRegion[] = [];
  subRegions: MapSubRegion[] = [];
  /** The Active sub-region picker and the List's filter: grouped by region. */
  subRegionGroups: OptionGroup[] = [];
  subRegionNames = new Map<string, string>();

  private assignOptionsKey = '';
  private assignOptions: Option[] = [];

  constructor(
    private api: SuburbAreasService,
    private state: WorkspaceStateService
  ) {}

  apply(meta: Meta): void {
    this.regions = mapRegions(meta.regions, cssToken());
    this.regionNames = new Map(meta.regions.map((r) => [r.id, r.name]));
    this.regionOptions = [
      { label: 'No region', value: 'none' },
      ...meta.regions.map((r) => ({ label: r.name, value: r.id })),
    ];
    this.pageRefs = meta.pages;
    this.subRegionRefs = meta.subRegions ?? [];
    this.subRegions = mapSubRegions(this.subRegionRefs, cssToken());
    this.subRegionNames = new Map(this.subRegionRefs.map((s) => [s.id, s.name]));
    this.subRegionGroups = meta.regions
      .map((r) => ({
        label: r.name,
        items: this.subRegionsOf(r.id).map((s) => ({ label: s.name, value: s.id })),
      }))
      .filter((g) => g.items.length);
  }

  /** The suburbs with a website page, for the map's dots (salCode -> its region and label). */
  pagesMap(): Map<string, MapPage> {
    return new Map(this.pageRefs.map((p) => [p.salCode, { regionId: p.regionId, label: p.label }]));
  }

  /** Re-read regions, sub-regions and pages after a region or page save, and show them on the map. */
  async reload(): Promise<void> {
    const meta = await this.api.meta().toPromise();
    this.apply(meta);
    const engine = this.state.engine;
    engine.setRegions(this.regions);
    engine.setSubRegions(this.subRegions);
    engine.setPages(this.pagesMap());
    this.state.mapChanged();
    this.state.redraw();
  }

  regionName(id: string | null | undefined): string {
    return (id && this.regionNames.get(id)) || id || '';
  }

  subRegionName(id: string | null | undefined): string {
    return (id && this.subRegionNames.get(id)) || id || '';
  }

  subRegionsOf(regionId: string): SuburbSubRegion[] {
    return this.subRegionRefs.filter((s) => s.regionId === regionId);
  }

  subRegionRef(id: string | null | undefined): SuburbSubRegion | undefined {
    return this.subRegionRefs.find((s) => s.id === id);
  }

  pageOf(code: string | null): SuburbPageRef | undefined {
    return code ? this.pageRefs.find((p) => p.salCode === code) : undefined;
  }

  regionColour(r: MapRegion): string {
    return rgbCss(r.rgb);
  }

  subRegionColour(id: string | null | undefined): string {
    const s = this.state.engine?.subRegionById(id) ?? this.subRegions.find((x) => x.id === id);
    return s ? rgbCss(s.rgb) : 'transparent';
  }

  /** A sub-region's colour slot (1–8): its own, else the next in its region's list. */
  colourOf(id: string): number {
    const ref = this.subRegionRef(id);
    const i = this.subRegionsOf(ref?.regionId ?? '').findIndex((s) => s.id === id);
    return colourSlot(ref?.mapColour, Math.max(i, 0));
  }

  /** Assign mode's Sub-region picker: None first, then the active region's sub-regions (rebuilt only when those change). */
  get assignSubRegionOptions(): Option[] {
    const regionId = this.state.activeRegion;
    const subs = regionId ? this.subRegionsOf(regionId) : [];
    const key = `${regionId}|${subs.map((s) => `${s.id}:${s.name}`).join(',')}`;
    if (key !== this.assignOptionsKey) {
      this.assignOptionsKey = key;
      this.assignOptions = [
        { label: 'None (region only)', value: '' },
        ...subs.map((s) => ({ label: s.name, value: s.id })),
      ];
    }
    return this.assignOptions;
  }

  /**
   * "Add to sub-region…" on a suburb's Overview: its region's sub-regions it isn't in; for a suburb in no region every
   * sub-region, grouped by region (joining one puts it in that region too).
   */
  addableSubRegions(area: { regionId: string | null; subRegions: string[] }): OptionGroup[] {
    const fits = (id: string) =>
      !area.subRegions.includes(id) &&
      (!area.regionId || this.subRegionRef(id)?.regionId === area.regionId);
    return this.subRegionGroups
      .map((g) => ({ ...g, items: g.items.filter((i) => fits(i.value)) }))
      .filter((g) => g.items.length);
  }
}
